import {ClassStep, School, SchoolClass, SchoolClassGroup} from "models"
import React, {useMemo, useRef} from "react"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component"
import Button from "components/inputs/button"
import {hasFilterInQueryParams} from "components/user/unicorn/filter-changed"
import HayaSelect from "components/woof-haya-select"
import I18n from "shared/i18n"
import Input from "components/inputs/input"
import isUserA from "components/users/is-a"
import {loading} from "components/layout/loading-blocker"
import memo from "set-state-compare/build/memo"
import Params from "@kaspernj/api-maker/build/params"
import {Platform} from "react-native"
import PropTypes from "prop-types"
import propTypesExact from "prop-types-exact"
import useCurrentUser from "@kaspernj/api-maker/build/use-current-user"
import useQueryParams from "on-location-changed/build/use-query-params"

const schoolClassGroupsEnabled = window.RAILS_ENV == "test"
const dateInputType = window.RAILS_ENV == "test" ? "text" : "date"

export default memo(shapeComponent(class ComponentsUserUnicornFilter extends ShapeComponent {
  static defaultProps = {
    classStepIds: null,
    dateFrom: null,
    dateTo: null,
    schoolClassIds: null,
    schoolClassGroupIds: null,
    schoolIds: null
  }

  static propTypes = propTypesExact({
    classStepIds: PropTypes.array,
    dateFrom: PropTypes.instanceOf(Date),
    dateTo: PropTypes.instanceOf(Date),
    schoolClassIds: PropTypes.array,
    schoolClassGroupIds: PropTypes.array,
    schoolIds: PropTypes.array
  })

  setup() {
    this.currentUser = useCurrentUser()
    this.queryParams = useQueryParams()
    this.dateFromInputRef = useRef()
    this.dateToInputRef = useRef()

    this.useStates({
      dateFrom: () => this.p.dateFrom,
      dateTo: () => this.p.dateTo,
      schoolClassesChanges: undefined,
      defaultIds: this.loadDefaultIds(),
      classSteps: undefined,
      schools: undefined,
      schoolClasses: undefined,
      schoolClassGroups: undefined
    })

    this.dateFromText = this.formatDate(this.p.dateFrom)
    this.dateToText = this.formatDate(this.p.dateTo)
    this.dateFromTextTouched = false
    this.dateToTextTouched = false

    useMemo(() => {
      if (!this.currentUser) {
        return
      }

      this.loadClassSteps()
      this.loadSchools()
      this.loadSchoolClasses()

      if (schoolClassGroupsEnabled) {
        this.loadSchoolClassGroups()
      }
    }, [this.currentUser.id()])

    useMemo(() => {
      if (this.queryParams.date_from) {
        this.setState({
          dateFrom: new Date(this.queryParams.date_from)
        })

        this.dateFromText = this.queryParams.date_from
        this.dateFromTextTouched = false
      }
    }, [this.queryParams.date_from])

    useMemo(() => {
      if (this.queryParams.date_to) {
        this.setState({
          dateTo: new Date(this.queryParams.date_to)
        })

        this.dateToText = this.queryParams.date_to
        this.dateToTextTouched = false
      }
    }, [this.queryParams.date_to])

    useMemo(() => {
      if (this.p.dateFrom) {
        this.setState({
          dateFrom: this.p.dateFrom
        })

        this.dateFromText = this.formatDate(this.p.dateFrom)
        this.dateFromTextTouched = false
      }

      if (this.p.dateTo) {
        this.setState({
          dateTo: this.p.dateTo
        })

        this.dateToText = this.formatDate(this.p.dateTo)
        this.dateToTextTouched = false
      }
    }, [this.p.dateFrom, this.p.dateTo])
  }

  loadClassSteps = async () => {
    const loadingLabel = ClassStep.modelName().human({count: 2})

    await loading(loadingLabel, async () => {
      const classSteps = await ClassStep
        .ransack({s: "primary_school_step"})
        .select({ClassStep: ["id", "name"]})
        .toArray()

      this.setState({classSteps})
    })
  }

  loadDefaultIds = () => {
    const {classStepIds, schoolClassIds, schoolClassGroupIds, schoolIds} = this.p
    const defaultIds = [...classStepIds || [], ...schoolClassIds || [], ...schoolClassGroupIds || [], ...schoolIds || []]

    return defaultIds
  }

  loadSchools = async () => {
    const loadingLabel = School.modelName().human({count: 2})

    await loading(loadingLabel, async () => {
      const schools = await School
        .ransack({
          s: "current_translation_name",
          with_contact_id: this.tt.currentUser.contactId()
        })
        .select({School: ["hasProSubscription", "id", "name"]})
        .groupBy("id")
        .toArray()

      this.setState({schools})
    })
  }

  loadSchoolClasses = async () => {
    const loadingLabel = SchoolClass.modelName().human({count: 2})

    await loading(loadingLabel, async () => {
      const schoolClasses = await SchoolClass
        .ransack({
          s: "current_translation_name",
          with_contact_id: this.tt.currentUser.contactId()
        })
        .select({SchoolClass: ["hasProSubscription", "id", "interpretedName"]})
        .groupBy("id")
        .toArray()

      this.setState({schoolClasses})
    })
  }

  loadSchoolClassGroups = async () => {
    const loadingLabel = SchoolClassGroup.modelName().human({count: 2})

    await loading(loadingLabel, async () => {
      const schoolClassGroups = await SchoolClassGroup
        .ransack({s: "current_translation_name"})
        .select({SchoolClassGroup: ["id", "name"]})
        .toArray()

      this.setState({schoolClassGroups})
    })
  }

  render() {
    return (
      <div className="components--user--unicorn--filter">
        <form onSubmit={this.tt.onSubmit}>
          <div style={{display: "flex", flexWrap: "wrap"}}>
            <div className="school-class-select-container">
              {this.s.schoolClasses && (!schoolClassGroupsEnabled || this.s.schoolClassGroups) && this.s.defaultIds !== undefined &&
                <HayaSelect
                  className="school-classes-select"
                  defaultValues={this.s.defaultIds}
                  id="school-classes-select"
                  multiple
                  onChange={this.tt.onSchoolClassesChanged}
                  options={this.schoolClassesOptions()}
                  woofInputStyling
                />
              }
            </div>
            <div className="date-from-container">
              <Input
                defaultValue={this.formatDate(this.s.dateFrom)}
                id="date_from"
                inputRef={this.dateFromInputRef}
                onChange={this.tt.onDateFromChanged}
                style={{width: "100%"}}
                type={dateInputType}
              />
            </div>
            <div className="date-to-container">
              <Input
                defaultValue={this.formatDate(this.s.dateTo)}
                id="date_to"
                inputRef={this.dateToInputRef}
                onChange={this.tt.onDateToChanged}
                style={{width: "100%"}}
                type={dateInputType}
              />
            </div>
            <div className="search-submit-container">
              <Button className="search-submit" label={I18n.t("js.shared.search")} primary />
            </div>
          </div>
        </form>
      </div>
    )
  }

  onDateFromChanged = (e) => {
    const refValue = Platform.OS == "web" ? this.dateFromInputRef.current?.value : undefined
    let value = e?.target?.value ?? e?.currentTarget?.value ?? e?.nativeEvent?.text ?? e?.value ?? ""

    if (refValue && (!value || refValue.length >= value.length)) {
      value = refValue
    }

    if (typeof value !== "string") return

    this.dateFromText = value
    this.dateFromTextTouched = true
  }

  onDateToChanged = (e) => {
    const refValue = Platform.OS == "web" ? this.dateToInputRef.current?.value : undefined
    let value = e?.target?.value ?? e?.currentTarget?.value ?? e?.nativeEvent?.text ?? e?.value ?? ""

    if (refValue && (!value || refValue.length >= value.length)) {
      value = refValue
    }

    if (typeof value !== "string") return

    this.dateToText = value
    this.dateToTextTouched = true
  }

  onSchoolClassesChanged = ({options}) => {
    this.setState({schoolClassesChanges: options})
  }

  onSubmit = (e) => {
    e.preventDefault()

    const newParams = {}
    const dateFromValue = Platform.OS == "web" ? this.dateFromInputRef.current?.value : undefined
    const dateToValue = Platform.OS == "web" ? this.dateToInputRef.current?.value : undefined
    let dateFromText = null
    let dateToText = null

    if (typeof dateFromValue == "string") {
      dateFromText = dateFromValue
    } else if (this.dateFromTextTouched) {
      dateFromText = this.dateFromText
    }

    if (typeof dateToValue == "string") {
      dateToText = dateToValue
    } else if (this.dateToTextTouched) {
      dateToText = this.dateToText
    }
    const dateFrom = dateFromText !== null ? this.validDateFromText(dateFromText) : this.s.dateFrom
    const dateTo = dateToText !== null ? this.validDateFromText(dateToText) : this.s.dateTo

    // Get class steps, school classes, schools and school class groups
    let options

    if (this.s.schoolClassesChanges) {
      options = this.s.schoolClassesChanges
    } else if (!hasFilterInQueryParams(this.queryParams)) {
      // Insert defaults into params if this is the first filtering
      options = this.selectedOptionsFromDefaultIds()
    }

    if (options) {
      const classStepIds = options.filter((option) => option.model == "class-step").map((option) => option.value)
      const schoolIds = options.filter((option) => option.model == "school").map((option) => option.value)
      const schoolClassesIds = options.filter((option) => option.model == "school-class").map((option) => option.value)
      let schoolClassGroupsIds

      if (schoolClassGroupsEnabled) {
        schoolClassGroupsIds = options.filter((option) => option.model == "school-class-group").map((option) => option.value)
      }

      this.setState({schoolClassesChanges: undefined})

      newParams.class_steps = classStepIds
      newParams.schools = schoolIds
      newParams.school_classes = schoolClassesIds

      if (schoolClassGroupsEnabled) {
        newParams.school_class_groups = schoolClassGroupsIds
      }
    }

    // Date filters
    if (dateFrom) {
      newParams.date_from = I18n.strftime("%Y-%m-%d", new Date(dateFrom))
    } else {
      newParams.date_from = undefined
    }

    if (dateTo) {
      newParams.date_to = I18n.strftime("%Y-%m-%d", new Date(dateTo))
    } else {
      newParams.date_to = undefined
    }

    if (Object.keys(newParams).length > 0) {
      Params.changeParams(newParams)
    }
  }

  selectedOptionsFromDefaultIds = () => {
    const options = []

    for (const defaultId of this.s.defaultIds) {
      const classStep = this.s.classSteps.find((classStep) => classStep.id() == defaultId)
      const school = this.s.schools.find((school) => school.id() == defaultId)
      const schoolClass = this.s.schoolClasses.find((schoolClass) => schoolClass.id() == defaultId)
      let schoolClassGroup

      if (schoolClassGroupsEnabled) {
        schoolClassGroup = this.s.schoolClassGroups.find((schoolClassGroup) => schoolClassGroup.id() == defaultId)
      }

      let model

      if (classStep) {
        model = "class-step"
      } else if (school) {
        model = "school"
      } else if (schoolClass) {
        model = "school-class"
      } else if (schoolClassGroup) {
        model = "school-class-group"
      }

      if (model) options.push({model, value: defaultId})
    }

    return options
  }

  formatDate(date) {
    return date ? I18n.strftime("%Y-%m-%d", date) : ""
  }

  validDateFromText(value) {
    if (!value) return null

    const parsedDate = new Date(value)

    if (Number.isNaN(parsedDate.getTime())) {
      return null
    }

    return parsedDate
  }

  schoolClassesOptions = () => {
    const options = []

    options.push({
      key: "schools-group",
      text: School.modelName().human({count: 2}),
      type: "group"
    })

    if (this.s.schools) {
      for (const school of this.s.schools) {
        options.push({
          disabled: !isUserA(this.currentUser, "admin") && !school.hasProSubscription(),
          model: "school",
          text: school.name(),
          value: school.id()
        })
      }
    }

    options.push({
      key: "class-steps-group",
      text: ClassStep.modelName().human({count: 2}),
      type: "group"
    })

    for (const classStep of this.s.classSteps) {
      options.push({
        model: "class-step",
        text: classStep.name(),
        value: classStep.id()
      })
    }

    if (schoolClassGroupsEnabled) {
      options.push({
        key: "school-class-groups",
        text: SchoolClassGroup.modelName().human({count: 2}),
        type: "group"
      })

      if (this.s.schoolClassGroups) {
        options.push(...this.s.schoolClassGroups.map((schoolClassGroup) => ({
          model: "school-class-group",
          text: schoolClassGroup.name(),
          value: schoolClassGroup.id()
        })))
      }
    }

    options.push({
      key: "school-classes",
      text: SchoolClass.modelName().human({count: 2}),
      type: "group"
    })

    if (this.s.schoolClasses) {
      options.push(...this.s.schoolClasses.map((schoolClass) => ({
        disabled: !isUserA(this.currentUser, "admin") && !schoolClass.hasProSubscription(),
        model: "school-class",
        text: schoolClass.interpretedName(),
        value: schoolClass.id()
      })))
    }

    return options
  }
}))
